import { Link } from 'react-router-dom';
import { 
  FileText, Shield, TrendingUp, Code, Palette, Building2, Users, 
  BarChart3, CheckCircle, Sparkles, Zap, Globe, Key, LineChart,
  CreditCard, Scale, Briefcase, ArrowRight
} from 'lucide-react';
import creditReportMoneyLogo from '@/assets/credit-report-money-logo.png';
import { useEffect, useRef } from 'react';

export default function Index() {
  const cardsRef = useRef<HTMLDivElement>(null);

  // Flashlight card mouse tracking effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const cards = document.querySelectorAll('.flashlight-card');
      cards.forEach((card) => {
        const rect = (card as HTMLElement).getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        (card as HTMLElement).style.setProperty('--mouse-x', `${x}px`);
        (card as HTMLElement).style.setProperty('--mouse-y', `${y}px`);
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden selection:bg-primary/30">
      {/* Nebula Background */}
      <div className="fixed -z-50 overflow-hidden w-full h-full top-0 right-0 bottom-0 left-0">
        <div 
          className="w-full h-full absolute top-0 right-0 bottom-0 left-0" 
          style={{
            background: 'linear-gradient(rgb(0, 24, 113) 0%, rgb(0, 82, 194) 25%, rgb(74, 150, 228) 45%, rgb(217, 230, 245) 65%, rgb(255, 173, 125) 75%, rgb(247, 93, 77) 85%, rgb(204, 0, 61) 95%, rgb(82, 0, 61) 100%)'
          }}
        />
        <div 
          className="pointer-events-none opacity-40 mix-blend-screen absolute top-0 right-0 bottom-0 left-0 animate-aurora"
          style={{
            background: 'radial-gradient(circle at 50% 100%, #38bdf8, transparent 60%)'
          }}
        />
        <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-[#020412] to-transparent opacity-90" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#020412]/20 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src={creditReportMoneyLogo}
              alt="Credit Report Money"
              className="h-12 w-auto"
            />
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-300 hover:text-foreground transition-colors">Features</a>
            <a href="#pricing" className="text-sm text-slate-300 hover:text-foreground transition-colors">Pricing</a>
            <a href="#use-cases" className="text-sm text-slate-300 hover:text-foreground transition-colors">Use Cases</a>
            <Link to="/docs" className="text-sm text-slate-300 hover:text-foreground transition-colors">API Docs</Link>
            <Link to="/demo" className="text-sm text-slate-300 hover:text-foreground transition-colors">Request Demo</Link>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/auth">
              <button className="px-5 py-2 text-sm rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-foreground hover:bg-white/10 transition-all duration-300 font-medium">
                Login
              </button>
            </Link>
            <Link to="/auth">
              <button className="px-5 py-2 text-sm rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 font-medium shadow-lg shadow-primary/25">
                Start Free Trial
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 min-h-[90vh] flex items-center overflow-hidden isolate">
        <div className="absolute inset-0 z-0 bg-background">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          >
            <source src="https://storage.googleapis.com/msgsndr/FuOewPgnMEW1CaeIftBR/media/6944c9a85b256bbfd9c619bc.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#020412]/80 via-[#020412]/60 to-[#020412] z-[1]" />
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="animate-intro">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full border border-primary/40 bg-primary/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium text-sm">Enterprise-Grade Credit Analysis API</span>
            </div>
          </div>
          
          <h1 className="animate-intro text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight" style={{ animationDelay: '0.1s' }}>
            <span className="title-reflection">Embed Powerful Credit</span>
            <br />
            <span className="title-reflection">Analysis Into Your Platform</span>
          </h1>
          
          <p className="animate-intro text-lg sm:text-xl text-slate-300 font-light max-w-3xl mx-auto mb-8 leading-relaxed" style={{ animationDelay: '0.2s' }}>
            White-label AI-powered credit report analysis for credit repair companies, financial advisors, and fintech platforms. 
            <span className="text-foreground font-medium"> Your brand. Your clients. Our technology.</span>
          </p>
          
          <div className="animate-intro flex flex-wrap justify-center gap-3 mb-10" style={{ animationDelay: '0.3s' }}>
            <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 backdrop-blur-sm text-sm font-medium">Credit Repair</span>
            <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 backdrop-blur-sm text-sm font-medium">Financial Advisory</span>
            <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 backdrop-blur-sm text-sm font-medium">Fintech</span>
            <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 backdrop-blur-sm text-sm font-medium">Legal Services</span>
          </div>
          
          <div className="animate-intro flex flex-col sm:flex-row items-center justify-center gap-4" style={{ animationDelay: '0.4s' }}>
            <Link to="/auth">
              <button className="btn-pill btn-glow bg-primary text-primary-foreground font-semibold text-lg">
                Start Free Trial
              </button>
            </Link>
            <a href="#pricing">
              <button className="btn-pill border border-white/10 bg-white/5 backdrop-blur-sm text-foreground hover:bg-white/10 font-medium transition-all">
                View Pricing
              </button>
            </a>
          </div>
          
          <p className="animate-intro text-sm text-slate-400 mt-6" style={{ animationDelay: '0.5s' }}>
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 px-6 section-border">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-slate-400 mb-10 font-light">Trusted by leading organizations</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '10K+', label: 'Reports Analyzed' },
              { value: '500+', label: 'Active Organizations' },
              { value: '99.9%', label: 'API Uptime' },
              { value: '$2M+', label: 'Violations Detected' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 section-border">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-shimmer text-3xl sm:text-4xl lg:text-5xl font-semibold text-center mb-4 tracking-tight">How It Works</h2>
          <p className="text-center text-slate-400 mb-16 max-w-2xl mx-auto font-light text-lg">
            Get started in minutes with our simple integration process
          </p>
          <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto" ref={cardsRef}>
            {[
              { step: '1', icon: Key, title: 'Integrate', desc: 'Get your API key and embed our analyzer in minutes with our RESTful API or embeddable widget' },
              { step: '2', icon: Palette, title: 'Customize', desc: 'Apply your branding: logo, colors, and domain whitelist. Make it truly yours.' },
              { step: '3', icon: TrendingUp, title: 'Scale', desc: 'Pay per scan with no monthly minimums. Enterprise SLAs available for high-volume clients.' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/30 sonar-emitter">
                  <item.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-foreground">{item.title}</h3>
                <p className="text-slate-400 font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 section-border">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-shimmer text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight">Platform Features</h2>
            <p className="text-slate-400 font-light max-w-2xl mx-auto text-lg">
              Everything you need to deliver powerful credit analysis to your clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Palette,
                title: 'White-Label Branding',
                description: 'Your logo, colors, and custom domain. Clients see your brand, not ours.'
              },
              {
                icon: Code,
                title: 'RESTful API Access',
                description: 'Simple integration with any platform using our well-documented REST API.'
              },
              {
                icon: Globe,
                title: 'Embeddable Widget',
                description: 'Drop-in credit analyzer widget for your website with customizable styling.'
              },
              {
                icon: Building2,
                title: 'Organization Dashboard',
                description: 'Manage team members, API keys, usage analytics, and billing in one place.'
              },
              {
                icon: Shield,
                title: 'FCRA Violation Detection',
                description: 'Automatic detection of potential legal violations - a value-add for your clients.'
              },
              {
                icon: LineChart,
                title: 'Real-time Analytics',
                description: 'Track usage, monitor performance, and gain insights into your client base.'
              }
            ].map((feature, idx) => (
              <div
                key={idx}
                className="group flashlight-card rounded-3xl p-8 card-hover">
                <div className="relative z-10">
                  <feature.icon className="w-12 h-12 mb-5 text-primary" />
                  <h3 className="text-xl font-semibold mb-3 text-foreground">{feature.title}</h3>
                  <p className="text-slate-400 font-light leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-shimmer text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight">Built For Your Industry</h2>
            <p className="text-slate-400 font-light max-w-2xl mx-auto text-lg">
              Tailored solutions for credit-focused businesses
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Scale,
                title: 'Credit Repair Companies',
                description: 'Automate client intake with instant credit analysis. Identify disputable items and FCRA violations to build stronger cases.',
                benefits: ['Faster client onboarding', 'Automated violation detection', 'White-label client reports']
              },
              {
                icon: Briefcase,
                title: 'Financial Advisors',
                description: 'Offer credit insights as a value-add service. Help clients understand their credit health during financial planning.',
                benefits: ['Enhanced client value', 'Comprehensive credit snapshots', 'Cross-bureau comparison']
              },
              {
                icon: CreditCard,
                title: 'Fintech Platforms',
                description: 'Integrate credit analysis into lending workflows. Pre-qualify applicants and assess risk before underwriting.',
                benefits: ['API-first integration', 'Real-time analysis', 'Custom webhooks']
              },
              {
                icon: Users,
                title: 'Legal Services',
                description: 'Detect FCRA violations for case intake. Build stronger consumer protection cases with detailed violation reports.',
                benefits: ['Violation documentation', 'Settlement value estimates', 'Expert-ready reports']
              }
            ].map((useCase, idx) => (
              <div key={idx} className="flashlight-card rounded-3xl p-8">
                <div className="relative z-10">
                  <useCase.icon className="w-12 h-12 mb-5 text-primary" />
                  <h3 className="text-xl font-semibold mb-3 text-foreground">{useCase.title}</h3>
                  <p className="text-slate-400 font-light leading-relaxed mb-4">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.benefits.map((benefit, benefitIdx) => (
                      <li key={benefitIdx} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 section-border">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-shimmer text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight">Simple, Transparent Pricing</h2>
            <p className="text-slate-400 font-light max-w-2xl mx-auto text-lg">
              Start free, scale as you grow. No hidden fees.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                name: 'Free',
                price: '$0',
                period: 'forever',
                description: 'Perfect for testing and evaluation',
                features: ['10 scans/month', 'API access', 'Basic analytics', 'Community support'],
                cta: 'Get Started',
                highlighted: false
              },
              {
                name: 'Starter',
                price: '$49',
                period: '/month',
                description: 'For small teams getting started',
                features: ['100 scans/month', 'White-label branding', 'Priority support', 'Webhook integrations'],
                cta: 'Start Trial',
                highlighted: false
              },
              {
                name: 'Professional',
                price: '$199',
                period: '/month',
                description: 'For growing organizations',
                features: ['500 scans/month', 'Custom domain', 'Team management', 'Advanced analytics', 'API rate limits +'],
                cta: 'Start Trial',
                highlighted: true
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                period: '',
                description: 'For high-volume operations',
                features: ['Unlimited scans', 'Dedicated support', 'Custom SLA', 'On-premise option', 'Custom integrations'],
                cta: 'Contact Sales',
                highlighted: false
              }
            ].map((plan, idx) => (
              <div
                key={idx}
                className={`flashlight-card rounded-3xl p-8 relative ${plan.highlighted ? 'border-2 border-primary' : ''}`}>
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <div className="relative z-10">
                  <h3 className="text-xl font-semibold mb-2 text-foreground">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-slate-400">{plan.period}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-6 font-light">{plan.description}</p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/auth">
                    <button className={`w-full py-3 rounded-full font-medium transition-all ${
                      plan.highlighted 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                        : 'border border-white/10 bg-white/5 text-foreground hover:bg-white/10'
                    }`}>
                      {plan.cta}
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Preview */}
      <section id="integration" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flashlight-card rounded-3xl p-10 lg:p-14">
            <div className="relative z-10">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-shimmer text-3xl sm:text-4xl font-semibold mb-4 tracking-tight">Simple API Integration</h2>
                  <p className="text-slate-400 font-light mb-6 text-lg">
                    Get started with just a few lines of code. Our RESTful API is designed for developers.
                  </p>
                  <ul className="space-y-4 mb-8">
                    {[
                      'Comprehensive documentation',
                      'SDKs for popular languages',
                      'Sandbox environment for testing',
                      'Webhook support for real-time updates'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-slate-300">
                        <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/auth" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">
                    Get your API key <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="rounded-2xl bg-[#0d1117] p-6 font-mono text-sm overflow-x-auto border border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                    <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                  </div>
                  <pre className="text-slate-300">
{`// Analyze a credit report
const response = await fetch(
  'https://api.creditreportmoney.com/v1/analyze',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      report_url: 'https://...',
      client_reference: 'client_123'
    })
  }
);

const analysis = await response.json();
console.log(analysis.violations);`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FCRA Value Prop */}
      <section className="py-24 px-6 section-border">
        <div className="max-w-7xl mx-auto">
          <div className="flashlight-card rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-accent rounded-full blur-[150px]" />
            </div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-accent text-accent-foreground font-bold mb-6">
                  <Zap className="w-5 h-5" />
                  VALUE-ADD FOR YOUR CLIENTS
                </div>
                <h2 className="text-shimmer text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight">FCRA Violation Detection</h2>
                <p className="text-slate-400 font-light max-w-2xl mx-auto text-lg">
                  Help your clients identify potential legal violations and unlock new revenue opportunities
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  { title: 'Wrong Amounts', desc: 'Incorrect balances or payment amounts reported by creditors' },
                  { title: '1099-C Violations', desc: 'Debts reported after cancellation of debt (1099-C issued)' },
                  { title: 'Identity Theft', desc: 'Unauthorized accounts or fraudulent activity on credit reports' },
                  { title: 'Duplicate Accounts', desc: 'Same debt reported multiple times by different collectors' },
                  { title: 'Bankruptcy Discharge', desc: 'Discharged debts still showing as owed or active' },
                  { title: 'Background Screening', desc: 'Improper use of credit reports for employment or tenant screening' }
                ].map((violation, idx) => (
                  <div
                    key={idx}
                    className="flashlight-card rounded-2xl p-6">
                    <div className="relative z-10">
                      <h3 className="text-lg font-semibold mb-2 text-accent">{violation.title}</h3>
                      <p className="text-slate-400 text-sm font-light">{violation.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-shimmer text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6 tracking-tight">
            Ready to Transform Your Credit Services?
          </h2>
          <p className="text-slate-400 font-light text-lg mb-10 max-w-2xl mx-auto">
            Join hundreds of organizations already using our platform to deliver powerful credit analysis to their clients.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <button className="btn-pill btn-glow bg-primary text-primary-foreground font-semibold text-lg">
                Start Your Free Trial
              </button>
            </Link>
            <a href="mailto:sales@creditreportmoney.com">
              <button className="btn-pill border border-white/10 bg-white/5 backdrop-blur-sm text-foreground hover:bg-white/10 font-medium transition-all">
                Contact Sales
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="section-border bg-[#020412]/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src={creditReportMoneyLogo}
                alt="Credit Report Money"
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-slate-400 font-light">
                Enterprise-grade credit analysis API for modern businesses.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                <li><a href="#integration" className="hover:text-foreground transition-colors">API Docs</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#use-cases" className="hover:text-foreground transition-colors">Credit Repair</a></li>
                <li><a href="#use-cases" className="hover:text-foreground transition-colors">Financial Advisors</a></li>
                <li><a href="#use-cases" className="hover:text-foreground transition-colors">Fintech</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/auth" className="hover:text-foreground transition-colors">Login</Link></li>
                <li><a href="mailto:sales@creditreportmoney.com" className="hover:text-foreground transition-colors">Contact Sales</a></li>
                <li><a href="mailto:support@creditreportmoney.com" className="hover:text-foreground transition-colors">Support</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-slate-400">
            <p className="text-sm font-light">© 2024 Credit Report Money. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
